import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock3,
  Loader2,
  MapPinned,
  RefreshCw,
  Scan,
  ShieldAlert,
  Sparkles,
  Truck,
  X,
  XCircle,
} from 'lucide-react';
import DateSelector from '../control-tower/DateSelector';
import { socket } from '../chat/socket';
import {
  apiErrorMessage,
  assignDispatchReceptionSelection,
  fetchDispatchReceptionAIUsage,
  fetchDispatchReceptionDetail,
  fetchDispatchReceptionMap,
  previewDispatchReceptionSelection,
  reanalyzeDispatchReceptionDelivery,
  reviewDispatchReceptionDelivery,
} from './operationalIntelligenceApi';

const LIMA_CENTER = [-12.0464, -77.0428];
const BAND_META = {
  hot: { label: 'Alta 80–100', color: '#059669', classes: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  probable: { label: 'Probable 65–79', color: '#65a30d', classes: 'border-lime-200 bg-lime-50 text-lime-800' },
  unclear: { label: 'Por aclarar 40–64', color: '#d97706', classes: 'border-amber-200 bg-amber-50 text-amber-800' },
  low: { label: 'Baja 0–39', color: '#64748b', classes: 'border-slate-200 bg-slate-50 text-slate-700' },
  blocked: { label: 'En revisión', color: '#dc2626', classes: 'border-red-200 bg-red-50 text-red-800' },
};

const STATE_LABELS = {
  VERIFIED_CONFIRMED: 'Confirmación verificada',
  AI_CONFIRMED: 'IA: confirmación clara',
  AI_LIKELY_CONFIRMED: 'IA: confirmación probable',
  CUSTOMER_RESPONDED_UNCLEAR: 'Respuesta por aclarar',
  NO_CUSTOMER_RESPONSE: 'Sin respuesta del cliente',
  AI_NEGATIVE_REVIEW: 'IA: señal negativa por revisar',
  VERIFIED_NOT_AVAILABLE: 'No disponible verificado',
  CANCELLATION_HOLD: 'Solicitud de cancelación',
  POSTPONEMENT_HOLD: 'Solicitud de postergación',
  UNKNOWN: 'Sin contexto concluyente',
};

function todayInLima() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Lima', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function markerIcon(point, selected) {
  const meta = BAND_META[point.reception?.band] || BAND_META.low;
  const score = Number(point.reception?.score || 0);
  return L.divIcon({
    className: '',
    html: `<div style="width:36px;height:36px;border-radius:999px;background:${meta.color};color:white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:900;border:${selected ? '4px solid #0f172a' : '3px solid white'};box-shadow:0 7px 18px rgba(15,23,42,.32)">${score}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  const fittedSignature = useRef('');
  useEffect(() => {
    if (!points.length) return;
    const signature = points
      .map(point => `${point.id}:${point.latitude}:${point.longitude}`)
      .join('|');
    if (signature === fittedSignature.current) return;
    map.fitBounds(
      L.latLngBounds(points.map(point => [point.latitude, point.longitude])).pad(0.18),
      { maxZoom: 14 }
    );
    fittedSignature.current = signature;
  }, [map, points]);
  return null;
}

function RectangleSelector({ enabled, points, onComplete }) {
  const map = useMap();
  const layerRef = useRef(null);
  const startRef = useRef(null);
  const rectangleRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    layerRef.current = L.layerGroup().addTo(map);
    map.dragging.disable();
    map.getContainer().style.cursor = 'crosshair';

    const clear = () => {
      layerRef.current?.clearLayers();
      startRef.current = null;
      rectangleRef.current = null;
    };
    const preventMenu = event => event.preventDefault();
    const onDown = event => {
      if (event.originalEvent.button !== 0) return;
      event.originalEvent.preventDefault();
      startRef.current = event.latlng;
      rectangleRef.current = L.rectangle([event.latlng, event.latlng], {
        color: '#B1336E', weight: 2, fillOpacity: 0.15,
      }).addTo(layerRef.current);
    };
    const onMove = event => {
      if (startRef.current && rectangleRef.current) {
        rectangleRef.current.setBounds([startRef.current, event.latlng]);
      }
    };
    const onUp = event => {
      if (event.originalEvent.button !== 0 || !startRef.current || !rectangleRef.current) return;
      event.originalEvent.preventDefault();
      const box = L.latLngBounds(startRef.current, event.latlng);
      const ids = points
        .filter(point => point.assignable)
        .filter(point => box.contains(L.latLng(point.latitude, point.longitude)))
        .map(point => point.id);
      const bounds = {
        north: box.getNorth(), south: box.getSouth(), east: box.getEast(), west: box.getWest(),
      };
      clear();
      onComplete({ bounds, delivery_ids: ids });
    };

    map.getContainer().addEventListener('contextmenu', preventMenu);
    map.on('mousedown', onDown);
    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
    return () => {
      map.off('mousedown', onDown);
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      map.getContainer().removeEventListener('contextmenu', preventMenu);
      map.getContainer().style.cursor = '';
      map.dragging.enable();
      clear();
      layerRef.current?.remove();
    };
  }, [enabled, map, onComplete, points]);
  return null;
}

function formatWhen(value) {
  if (!value) return 'Sin registro';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('es-PE', {
    timeZone: 'America/Lima', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function DetailPanel({ point, currentDriverName, detail, loading, busy, onClose, onReview, onReanalyze }) {
  if (!point) return null;
  const reception = point.reception || {};
  const evidenceId = reception.assessment?.evidence_message_id;
  return (
    <aside className="fixed inset-y-0 right-0 z-[12000] flex w-full max-w-lg flex-col border-l border-slate-200 bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-slate-900 p-5 text-white">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-wider text-white/60">Entrega #{point.id}</p>
          <h3 className="mt-1 text-xl font-black">{point.recipient_name || 'Sin nombre'}</h3>
          <p className="mt-1 text-sm text-white/70">{point.district || 'Sin distrito'} · {point.seller_username || 'Sin tienda'}</p>
          <p className="mt-1 text-xs font-bold text-white/80">
            {point.driver_id
              ? `Conductor actual: ${currentDriverName || `#${point.driver_id}`}`
              : 'Sin conductor asignado'}
          </p>
        </div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-white/10"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto p-5">
        <div className={`rounded-2xl border p-4 ${BAND_META[reception.band]?.classes || BAND_META.low.classes}`}>
          <div className="flex items-center justify-between gap-3"><span className="text-xs font-extrabold uppercase">Preparación de recepción</span><strong className="text-3xl">{reception.score}</strong></div>
          <p className="mt-2 font-black">{STATE_LABELS[reception.state] || reception.state}</p>
          <p className="mt-1 text-xs">Puntaje heurístico explicable; no es todavía una probabilidad calibrada.</p>
        </div>

        {reception.blocked && (
          <div className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"><ShieldAlert className="h-5 w-5 shrink-0" /><div><strong>Despacho bloqueado para revisión</strong><p className="mt-1">{reception.hold_type || 'Señal negativa pendiente'}. No se cambió el estado de la entrega.</p></div></div>
        )}

        <section>
          <h4 className="text-sm font-black text-slate-900">Por qué tiene este puntaje</h4>
          <div className="mt-2 space-y-2">
            {(reception.contributions || []).map((row, index) => (
              <div key={`${row.source}-${index}`} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs"><span className="font-semibold text-slate-700">{row.reason_code}</span><strong className={row.delta > 0 ? 'text-emerald-700' : row.delta < 0 ? 'text-red-700' : 'text-slate-600'}>{row.delta > 0 ? '+' : ''}{row.delta}</strong></div>
            ))}
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between"><h4 className="text-sm font-black text-slate-900">Contexto reciente del chat</h4><button type="button" disabled={busy} onClick={onReanalyze} className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 px-2.5 py-1.5 text-xs font-bold text-violet-700 hover:bg-violet-50 disabled:opacity-50"><Sparkles className="h-3.5 w-3.5" />Reanalizar</button></div>
          {loading ? <Loader2 className="mx-auto mt-6 h-5 w-5 animate-spin text-brand-primary" /> : (
            <div className="mt-3 space-y-2">
              {(detail?.messages || []).length ? detail.messages.map(message => (
                <div key={message.id} className={`rounded-xl border p-3 text-xs ${message.id === evidenceId ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase text-slate-500"><span>{message.sender_type} · {message.channel}</span><span>{formatWhen(message.created_at)}</span></div>
                  <p className="mt-1 whitespace-pre-wrap leading-5 text-slate-800">{message.content || `[${message.message_type}]`}</p>
                  {message.id === evidenceId && <p className="mt-1 font-extrabold text-violet-700">Evidencia usada por el análisis</p>}
                </div>
              )) : <p className="rounded-xl bg-slate-50 p-4 text-xs text-slate-500">No hay mensajes entrantes utilizables.</p>}
            </div>
          )}
        </section>

        <section>
          <h4 className="text-sm font-black text-slate-900">Validación humana</h4>
          <p className="mt-1 text-xs text-slate-500">Estas acciones agregan evidencia; nunca cambian por sí solas el estado de la entrega.</p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button type="button" disabled={busy} onClick={() => onReview('confirmed')} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-2.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"><CheckCircle2 className="h-4 w-4" />Confirmó</button>
            <button type="button" disabled={busy} onClick={() => onReview('not_available')} className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-3 py-2.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"><XCircle className="h-4 w-4" />No disponible</button>
            <button type="button" disabled={busy} onClick={() => onReview('unclear')} className="rounded-xl border border-amber-300 px-3 py-2.5 text-xs font-bold text-amber-800 hover:bg-amber-50 disabled:opacity-50">Contexto incierto</button>
            <button type="button" disabled={busy || reception.state !== 'AI_NEGATIVE_REVIEW'} onClick={() => onReview('dismiss_ai_hold')} className="rounded-xl border border-slate-300 px-3 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40">Descartar bloqueo IA</button>
          </div>
        </section>
      </div>
    </aside>
  );
}

export default function DispatchReceptionMap({ initialDate, refreshSignal, onToast }) {
  const [date, setDate] = useState(initialDate || todayInLima);
  const [showCalendar, setShowCalendar] = useState(false);
  const [bands, setBands] = useState(new Set(['hot', 'probable', 'blocked']));
  const [payload, setPayload] = useState({ summary: {}, drivers: [], items: [] });
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drawing, setDrawing] = useState(false);
  const [selection, setSelection] = useState(null);
  const [driverId, setDriverId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);

  const bandQuery = useMemo(() => Array.from(bands).join(','), [bands]);
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const [mapData, usageData] = await Promise.all([
        fetchDispatchReceptionMap({ date, limit: 3000, include_assigned: true }),
        fetchDispatchReceptionAIUsage({ date }),
      ]);
      setPayload(mapData);
      setUsage(usageData);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo cargar el mapa de recepción'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load, refreshSignal]);
  useEffect(() => {
    const timer = window.setInterval(() => load({ quiet: true }), 60000);
    const realtime = event => {
      if (!event?.operational_date || event.operational_date === date) load({ quiet: true });
    };
    socket.on('delivery_reception_updated', realtime);
    socket.on('delivery_reception_assignments_updated', realtime);
    socket.on('delivery_intelligence_store_signal_updated', realtime);
    return () => {
      window.clearInterval(timer);
      socket.off('delivery_reception_updated', realtime);
      socket.off('delivery_reception_assignments_updated', realtime);
      socket.off('delivery_intelligence_store_signal_updated', realtime);
    };
  }, [date, load]);

  useEffect(() => {
    setSelection(null);
    setDrawing(false);
  }, [date, bandQuery]);

  const openPoint = useCallback(async point => {
    setSelectedPoint(point);
    setDetail(null);
    setDetailLoading(true);
    try {
      setDetail(await fetchDispatchReceptionDetail(point.id));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo cargar el contexto'));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const onRectangle = useCallback(async ({ bounds, delivery_ids }) => {
    setDrawing(false);
    if (!delivery_ids.length) {
      setError('No hay entregas asignables dentro del rectángulo.');
      return;
    }
    setError('');
    try {
      const preview = await previewDispatchReceptionSelection({
        bounds, delivery_ids, operational_date: date,
      });
      setSelection(preview);
      if (!preview.count) setError('La selección cambió o contiene bloqueos. Actualiza el mapa.');
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo validar la selección'));
    }
  }, [date]);

  async function assignSelection() {
    if (!selection?.count || !driverId) return;
    const reassignments = (selection.items || []).filter(
      item => item.driver_id && Number(item.driver_id) !== Number(driverId)
    ).length;
    const confirmation = reassignments
      ? `¿Asignar ${selection.count} entregas? ${reassignments} serán reasignadas desde otro conductor.`
      : `¿Asignar ${selection.count} entregas al conductor seleccionado?`;
    if (!window.confirm(confirmation)) return;
    setAssigning(true);
    setError('');
    try {
      const idempotencyKey = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      await assignDispatchReceptionSelection({
        selection_id: selection.selection_id,
        operational_date: selection.operational_date,
        bounds: selection.bounds,
        delivery_ids: selection.delivery_ids,
        driver_id: Number(driverId),
        idempotency_key: `reception-map:${idempotencyKey}`,
      });
      onToast?.(`${selection.count} entregas asignadas con trazabilidad.`);
      setSelection(null);
      setDriverId('');
      await load({ quiet: true });
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo completar la asignación'));
      if (requestError?.response?.status === 409) await load({ quiet: true });
    } finally {
      setAssigning(false);
    }
  }

  async function reviewPoint(decision) {
    if (!selectedPoint) return;
    setDetailBusy(true);
    try {
      await reviewDispatchReceptionDelivery(selectedPoint.id, { decision });
      onToast?.('Validación guardada como evidencia operativa.');
      await load({ quiet: true });
      const refreshed = await fetchDispatchReceptionDetail(selectedPoint.id);
      setDetail(refreshed);
      setSelectedPoint(refreshed.current || selectedPoint);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo registrar la validación'));
    } finally {
      setDetailBusy(false);
    }
  }

  async function reanalyzePoint() {
    if (!selectedPoint) return;
    setDetailBusy(true);
    try {
      await reanalyzeDispatchReceptionDelivery(selectedPoint.id);
      onToast?.('Análisis programado. El mapa se actualizará al terminar.');
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo programar el análisis'));
    } finally {
      setDetailBusy(false);
    }
  }

  const selectedIds = new Set(selection?.delivery_ids || []);
  const summary = payload.summary || {};
  const visibleItems = useMemo(
    () => (payload.items || []).filter(item => bands.has(item.reception?.band)),
    [payload.items, bands]
  );
  const driverNames = useMemo(
    () => new Map((payload.drivers || []).map(driver => [Number(driver.id), driver.username])),
    [payload.drivers]
  );
  const selectionBreakdown = useMemo(() => {
    const items = selection?.items || [];
    return {
      newAssignments: items.filter(item => !item.driver_id).length,
      assigned: items.filter(item => item.driver_id).length,
      reassignments: items.filter(
        item => item.driver_id && Number(item.driver_id) !== Number(driverId)
      ).length,
      unchanged: items.filter(
        item => item.driver_id && Number(item.driver_id) === Number(driverId)
      ).length,
    };
  }, [selection, driverId]);

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col justify-between gap-4 xl:flex-row xl:items-end">
          <div>
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-primary"><MapPinned className="h-4 w-4" /> Despacho por recepción</p>
            <h2 className="mt-1 text-xl font-black text-slate-900 sm:text-2xl">Entregas con mayor probabilidad de recepción</h2>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">Combina historial, comunicación, señales verificadas y una inferencia de chat acotada. La IA nunca asigna ni cambia estados.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <DateSelector selectedDate={date} onDateChange={setDate} showCalendar={showCalendar} onToggleCalendar={() => setShowCalendar(value => !value)} label="Fecha operativa" />
            <button type="button" onClick={() => load()} disabled={loading} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Actualizar</button>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {['hot', 'probable', 'unclear', 'low', 'blocked'].map(name => (
          <button key={name} type="button" onClick={() => setBands(current => { const next = new Set(current); if (next.has(name)) next.delete(name); else next.add(name); return next.size ? next : new Set([name]); })} className={`rounded-2xl border p-3 text-left transition ${bands.has(name) ? BAND_META[name].classes : 'border-slate-200 bg-white text-slate-400 opacity-60'}`}>
            <p className="text-[10px] font-extrabold uppercase">{BAND_META[name].label}</p><p className="mt-1 text-2xl font-black">{summary[name] || 0}</p>
          </button>
        ))}
        <div className="rounded-2xl border border-violet-200 bg-violet-50 p-3 text-violet-800"><p className="flex items-center gap-1 text-[10px] font-extrabold uppercase"><Bot className="h-3.5 w-3.5" /> IA hoy</p><p className="mt-1 text-2xl font-black">{usage?.requests || 0}</p><p className="text-[10px]">{usage?.total_tokens || 0} tokens</p></div>
      </section>

      {error && <div className="flex items-start justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700"><span className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{error}</span><button type="button" onClick={() => setError('')}><X className="h-4 w-4" /></button></div>}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col justify-between gap-3 border-b border-slate-200 p-4 lg:flex-row lg:items-center">
          <div><p className="font-black text-slate-900">Mapa de verdad operativa</p><p className="text-xs text-slate-500">Coordenadas exactas · estado pending/postergado · paquete in_fastmax · permite asignar y reasignar</p></div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">{visibleItems.length} puntos</span>
            <button type="button" onClick={() => { setSelection(null); setDrawing(value => !value); }} disabled={!visibleItems.some(item => item.assignable)} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-extrabold disabled:opacity-40 ${drawing ? 'bg-slate-900 text-white' : 'bg-brand-primary text-white'}`}><Scan className="h-4 w-4" />{drawing ? 'Cancelar rectángulo' : 'Seleccionar rectángulo'}</button>
          </div>
        </div>
        <div className="relative h-[620px] min-h-[480px]">
          {loading && <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/70"><div className="rounded-2xl bg-white p-5 text-center shadow"><Loader2 className="mx-auto h-7 w-7 animate-spin text-brand-primary" /><p className="mt-2 text-sm font-bold text-slate-600">Calculando preparación...</p></div></div>}
          <MapContainer center={LIMA_CENTER} zoom={11} scrollWheelZoom className="z-0 h-full w-full">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={visibleItems} />
            <RectangleSelector enabled={drawing} points={visibleItems} onComplete={onRectangle} />
            {visibleItems.map(point => (
              <Marker key={point.id} position={[point.latitude, point.longitude]} icon={markerIcon(point, selectedIds.has(point.id))} eventHandlers={{ click: () => openPoint(point) }}>
                <Popup><div className="min-w-[210px] text-sm"><strong>Entrega #{point.id} · {point.reception.score}</strong><p>{point.recipient_name}</p><p>{point.district || 'Sin distrito'} · {point.seller_username}</p><p className="mt-1 text-xs font-bold text-blue-700">{point.driver_id ? `Conductor: ${driverNames.get(Number(point.driver_id)) || `#${point.driver_id}`} · reasignable` : 'Sin conductor'}</p><p className="mt-1 font-semibold">{STATE_LABELS[point.reception.state] || point.reception.state}</p>{point.reception.blocked && <p className="mt-1 font-bold text-red-600">No asignable: revisión pendiente</p>}</div></Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </section>

      {selection && (
        <section className="sticky bottom-3 z-[1100] rounded-2xl border border-brand-primary/30 bg-white p-4 shadow-2xl">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div className="flex items-center gap-3"><div className="rounded-xl bg-brand-primary/10 p-2.5 text-brand-primary"><Truck className="h-5 w-5" /></div><div><p className="font-black text-slate-900">{selection.count} entregas validadas por servidor</p><p className="text-xs text-slate-500">{selectionBreakdown.newAssignments} sin conductor · {selectionBreakdown.assigned} con conductor{driverId ? ` (${selectionBreakdown.reassignments} se reasignarán${selectionBreakdown.unchanged ? ` y ${selectionBreakdown.unchanged} ya pertenecen al elegido` : ''})` : ''}. Se validarán nuevamente al confirmar.</p></div></div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select value={driverId} onChange={event => setDriverId(event.target.value)} className="min-w-[230px] rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold"><option value="">Selecciona conductor</option>{(payload.drivers || []).map(driver => <option key={driver.id} value={driver.id}>{driver.username} · #{driver.id}</option>)}</select>
              <button type="button" onClick={assignSelection} disabled={!driverId || assigning || !selection.count} className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-primary px-5 py-2.5 text-sm font-extrabold text-white disabled:opacity-50">{assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Asignar</button>
              <button type="button" onClick={() => setSelection(null)} className="rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-600">Cancelar</button>
            </div>
          </div>
        </section>
      )}

      {!loading && !visibleItems.length && <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center"><Clock3 className="mx-auto h-11 w-11 text-slate-300" /><h3 className="mt-3 text-lg font-black text-slate-800">Sin puntos para estos filtros</h3><p className="mt-1 text-sm text-slate-500">Activa “Por aclarar” o “Baja”, cambia la fecha o espera nueva evidencia.</p></div>}

      <DetailPanel point={selectedPoint} currentDriverName={driverNames.get(Number(selectedPoint?.driver_id))} detail={detail} loading={detailLoading} busy={detailBusy} onClose={() => { setSelectedPoint(null); setDetail(null); }} onReview={reviewPoint} onReanalyze={reanalyzePoint} />
    </div>
  );
}
